import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { Check, X, Filter, ChevronDown, ChevronRight, Copy, ExternalLink, FileImage, ShieldCheck } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const map = {
    completed: 'bg-[#00e676]/15 text-[#00e676]',
    pending:   'bg-amber-500/15 text-amber-400',
    rejected:  'bg-red-500/15 text-red-400'
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${map[status] || 'bg-gray-500/15 text-gray-400'}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
};

const CopyableRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs">
      <span className="text-[10px] uppercase tracking-wider text-gray-500 min-w-[110px]">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <code className="text-white mono truncate max-w-[420px]">{value}</code>
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
          className="p-1 rounded border border-[#1a2823] text-gray-400 hover:text-[#00e676] hover:border-[#00e676] shrink-0"
        >
          {copied ? <Check size={10}/> : <Copy size={10}/>}
        </button>
      </div>
    </div>
  );
};

const ProofPanel = ({ tx }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isDeposit = tx.type === 'deposit';
  const anyProof = tx.tx_hash || tx.sender_reference || tx.receipt_url || tx.deposit_account_label;
  return (
    <div className="p-4 bg-[#050807] border border-[#1a2823] rounded-lg space-y-2" data-testid={`proof-panel-${tx.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-[#00e676] font-semibold flex items-center gap-1">
          <ShieldCheck size={11}/> {isDeposit ? 'Deposit proof' : 'Withdrawal details'}
        </div>
        {tx.credited && (
          <span className="text-[10px] text-[#00e676] flex items-center gap-1">
            <Check size={10}/> Credited{tx.credited_at ? ` · ${new Date(tx.credited_at).toLocaleString()}` : ''}
          </span>
        )}
      </div>
      {isDeposit ? (
        <>
          {!anyProof && <div className="text-xs text-gray-500 italic">No additional proof submitted.</div>}
          <CopyableRow label="Destination"  value={tx.deposit_account_label}/>
          <CopyableRow label="TX Hash"      value={tx.tx_hash}/>
          <CopyableRow label="Sender ref"   value={tx.sender_reference}/>
          {tx.receipt_url && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
                <FileImage size={10}/> Receipt image
              </div>
              <button
                data-testid={`view-receipt-${tx.id}`}
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-2 group"
              >
                <img src={tx.receipt_url} alt="Receipt" className="w-16 h-16 object-cover rounded border border-[#1a2823] group-hover:border-[#00e676] transition"/>
                <span className="text-xs text-[#00e676] group-hover:underline inline-flex items-center gap-1">
                  Open full <ExternalLink size={10}/>
                </span>
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <CopyableRow label="Destination" value={tx.destination}/>
          <CopyableRow label="Method"      value={tx.method}/>
        </>
      )}
      {tx.admin_note && (
        <div className="mt-2 pt-2 border-t border-[#141f1b]">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Admin note</div>
          <div className="text-xs text-gray-300 mt-1">{tx.admin_note}</div>
        </div>
      )}

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setPreviewOpen(false)}
          data-testid={`receipt-modal-${tx.id}`}
        >
          <img src={tx.receipt_url} alt="Receipt" className="max-w-[90vw] max-h-[90vh] rounded-lg border border-[#00e676]"/>
        </div>
      )}
    </div>
  );
};

const AdminTransactions = ({ type = 'deposit' }) => {
  const [txs, setTxs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [noteFor, setNoteFor] = useState({});   // id -> note draft
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const params = { type };
      if (statusFilter) params.tx_status = statusFilter;
      setTxs(await adminAPI.transactions(params));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, statusFilter]);

  const update = async (id, status) => {
    try {
      const note = noteFor[id];
      await adminAPI.updateTx(id, status, note);
      toast({ title: `Transaction ${status}` });
      setNoteFor(prev => { const n = { ...prev }; delete n[id]; return n; });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' }); }
  };

  const isWithdraw = type === 'withdraw';
  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-6" data-testid={`admin-${type}s-page`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white capitalize">{type}s Management</h1>
          <p className="text-sm text-gray-400">Review proof of payment, then approve or reject.</p>
        </div>
        <div className="flex items-center gap-1">
          <Filter size={13} className="text-gray-500 mr-1"/>
          {['', 'pending', 'completed', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
                    data-testid={`filter-${s || 'all'}-btn`}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold ${statusFilter === s ? 'bg-[#00e676] text-black' : 'border border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="pl-2 pr-1 py-3 w-8"></th>
                <th className="pr-4 py-3">User</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Proof</th>
                {isWithdraw && <th>Destination</th>}
                <th>Status</th>
                <th>Date</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="py-6 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && txs.length === 0 && <tr><td colSpan="9" className="py-10 text-center text-sm text-gray-500">No {type}s found</td></tr>}
              {txs.map(t => {
                const isOpen = !!expanded[t.id];
                const hasProof = t.tx_hash || t.sender_reference || t.receipt_url;
                return (
                  <React.Fragment key={t.id}>
                    <tr className="border-t border-[#141f1b] hover:bg-[#0f1a17]" data-testid={`tx-row-${t.id}`}>
                      <td className="pl-2 pr-1">
                        <button onClick={() => toggle(t.id)} data-testid={`expand-${t.id}-btn`} className="p-1 text-gray-400 hover:text-[#00e676]">
                          {isOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                        </button>
                      </td>
                      <td className="pr-4 py-3">
                        <div className="text-sm text-white font-semibold">{t.user_name}</div>
                        <div className="text-[10px] text-gray-500">{t.user_email}</div>
                      </td>
                      <td className={`mono font-semibold ${type === 'deposit' ? 'text-[#00e676]' : 'text-red-400'}`}>
                        {type === 'deposit' ? '+' : '-'}${(t.amount || 0).toFixed(2)}
                      </td>
                      <td className="text-xs text-gray-300 uppercase">{t.method}</td>
                      <td className="text-xs">
                        {type === 'deposit' ? (
                          hasProof ? (
                            <span className="text-[#00e676] inline-flex items-center gap-1"><ShieldCheck size={11}/> yes</span>
                          ) : (
                            <span className="text-amber-400">missing</span>
                          )
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      {isWithdraw && <td className="text-xs text-gray-400 truncate max-w-[180px]">{t.destination || '—'}</td>}
                      <td><StatusBadge status={t.status}/></td>
                      <td className="text-xs text-gray-400 mono">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                      <td className="text-right pr-4">
                        {t.status !== 'completed' && (
                          <button onClick={() => update(t.id, 'completed')}
                                  data-testid={`approve-${t.id}-btn`}
                                  className="text-[11px] px-2.5 py-1 rounded border border-[#00e676]/40 text-[#00e676] font-semibold hover:bg-[#00e676]/10 mr-1 inline-flex items-center gap-1">
                            <Check size={11}/> Approve
                          </button>
                        )}
                        {t.status !== 'rejected' && (
                          <button onClick={() => update(t.id, 'rejected')}
                                  data-testid={`reject-${t.id}-btn`}
                                  className="text-[11px] px-2.5 py-1 rounded border border-red-500/40 text-red-400 font-semibold hover:bg-red-500/10 inline-flex items-center gap-1">
                            <X size={11}/> Reject
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-[#141f1b] bg-[#08110e]">
                        <td colSpan="9" className="px-6 py-4">
                          <div className="grid md:grid-cols-2 gap-3">
                            <ProofPanel tx={t}/>
                            <div className="p-4 bg-[#050807] border border-[#1a2823] rounded-lg space-y-2">
                              <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Admin note (optional)</div>
                              <textarea
                                value={noteFor[t.id] || ''}
                                onChange={e => setNoteFor(prev => ({ ...prev, [t.id]: e.target.value }))}
                                placeholder="Add a note before approving or rejecting (visible to user & audit)"
                                rows={4}
                                data-testid={`admin-note-${t.id}`}
                                className="w-full bg-[#0a1210] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"
                              />
                              <div className="text-[10px] text-gray-500">
                                Approving credits the trader's balance{type === 'deposit' ? ' and any referral commission' : ''} instantly.
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTransactions;
